import { Box, TextField, FormControl, InputLabel, Select, MenuItem, IconButton, SelectChangeEvent, Stack, Divider, Button, Tooltip, Chip } from '@mui/material';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIsShowModelModal, getModelDescription, getModelId, getModelName, getSchemaItems, setSchemaDescription, setSchemaName } from '../reducers/schema/schemaReducer';
import { addStructureItem, closeModelModalAction, removeStructureItem, updateStructureItem } from '../sagas/modelSaga/modelSagaActions';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { SchemaItem, SchemaItemCategory, StructureColumnTypeEnum, StructureItemType } from '../@shared/Models/Models';
import { ModalBase } from '../components/ModalBase';
import { useSchema } from '../hooks/useSchema';
import { Palette, Sizes } from '../@shared/palette';
import { FlexBoxColumn, InputBaseSx, LinkSxSmall, PrimaryBtnBorderlessSx, SecondaryBtnSx, SelectMenuItemSx, SelectMenuProps, TooltipSlotProps } from '../@shared/templates';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ContextHelper } from '../components/ContextHelper';
import { parseFileSchema } from '../@shared/schemaDataParser';
import { onThrowAlert } from '../sagas/dataSaga/dataSagaActions';
import { GuideStepEnum, NotificationModel, NotificationModelType } from '../reducers/main/mainModel';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GuideCallout from '../components/GuideCallout';
import { getGuideStep, setGuideStep } from '../reducers/main/mainReducer';

export const withAddOrUpdateSchema = <T extends Record<string, unknown>>(
  Component: React.ComponentType<T>
): React.FC<T> => (props) => {
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  const [isSchemaChanged, setIsSchemaChanged] = React.useState(false);
  const items = useSelector(getSchemaItems);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const guideStep = useSelector(getGuideStep);
  const [baseSchema, setBaseSchema] = React.useState<SchemaItem[]>([])

  React.useEffect(() => {
    if (baseSchema.length === 0 && items.length > 0) {
      setBaseSchema(items);
    }

    const sort = (a: string, b: string) => {
      if (a < b) { return -1; }
      if (a > b) { return 1; }
      return 0;
    }

    //Deep Compare
    const baseSchemaString = JSON.stringify(baseSchema?.filter(x => x.typeId !== StructureColumnTypeEnum.Info)?.sort((a, b) => sort(a.name, b.name)));
    const currentSchemaString = JSON.stringify(items?.filter(x => x.typeId !== StructureColumnTypeEnum.Info)?.sort((a, b) => sort(a.name, b.name)));

    setIsSchemaChanged(baseSchemaString !== currentSchemaString)
  }, [baseSchema, items]);

  const dispatch = useDispatch();
  const handleClose = () => dispatch(closeModelModalAction())
  const onAddItem = () => dispatch(addStructureItem())
  const { onSaveSchema, isProcessing, onAddListOfSchemaItems } = useSchema();
  const nameValue = useSelector(getModelName);
  const description = useSelector(getModelDescription);
  const isShowConfirm = !!useSelector(getModelId) && isSchemaChanged;

  const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setSchemaName(e.target.value))
  }

  const onChangeDescription = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    dispatch(setSchemaDescription(e.target.value))
  }

  const handleLoadFile = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        const result = parseFileSchema(content);
        if (result.isValid) {
          onAddListOfSchemaItems(result.schemaItems);
        } else {
          dispatch(onThrowAlert({
            message: result.error || "Failed to parse file. Please ensure it's a valid CSV or JSON schema file.",
            lifeTime: 15000,
            type: NotificationModelType.Error
          } as NotificationModel));
        }
      }
    };
    reader.readAsText(file);
    event.target.value = "";
  }, [dispatch, onAddListOfSchemaItems]);

  const isOpen = useSelector(getIsShowModelModal);

  const fillExample = () => {
    dispatch(setSchemaName("Example_Model"));
    dispatch(setSchemaDescription("This is an example model description."));
    onAddListOfSchemaItems([
      { name: "accountAgeDays", valueTypeId: StructureItemType.Number, typeId: StructureColumnTypeEnum.Feature },
      { name: "monthlyUsageHours", valueTypeId: StructureItemType.Number, typeId: StructureColumnTypeEnum.Feature },
      { name: "supportTickets", valueTypeId: StructureItemType.Number, typeId: StructureColumnTypeEnum.Feature },
      { name: "paymentFailures", valueTypeId: StructureItemType.Number, typeId: StructureColumnTypeEnum.Feature },
      { name: "subscriptionType", valueTypeId: StructureItemType.Category, typeId: StructureColumnTypeEnum.Feature, categories: [{ value: "free", index: 0 }, { value: "pro", index: 1 }] },
      { name: "country", valueTypeId: StructureItemType.Category, typeId: StructureColumnTypeEnum.Feature, categories: [{ value: "US", index: 0 }, { value: "UK", index: 1 }, { value: "DE", index: 2 }, { value: "FR", index: 3 }, { value: "Other", index: 4 }] },
      { name: "churned", valueTypeId: StructureItemType.Category, typeId: StructureColumnTypeEnum.Target, categories: [{ value: "no", index: 0 }, { value: "yes", index: 1 }] },
    ]);
    dispatch(setGuideStep(GuideStepEnum.SaveSchema));
  }

  return <>
    <ModalBase
      isOpen={isOpen}
      onCancel={handleClose}
      onSave={() => isShowConfirm ? setIsConfirmOpen(true) : onSaveSchema(false)}
      title="Schema"
      description="A schema defines the structure of your model's inputs and outputs. It ensures that your datasets and predictions follow the same format, making training and inference consistent and easy to manage."
      isProcessing={isProcessing}
      isDisabledSave={isProcessing}
      saveBtnTooltip={guideStep === GuideStepEnum.SaveSchema ? <GuideCallout>Click to save your schema. If you have made changes to an existing schema, you will be prompted to confirm the update, as it will remove all existing data and models associated with this schema.</GuideCallout> : undefined}
    >
      <ConfirmDialog isProcessing={isProcessing} isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} title='Update schema confirmation' description='After the schema updating all data and models will be removed. Do you confirm updating?' onConfirmedAction={ async () => {
        setIsConfirmOpen(false);
        await onSaveSchema(true);
      }} />

      <Box sx={{ marginBottom: "10px" }}>
        <Tooltip placement="left-start" open={guideStep === GuideStepEnum.FillSchemaForm} slotProps={TooltipSlotProps} title={
          <GuideCallout>
            Type the name of your model. The name should be descriptive and unique, as it will help you identify this model later when you have multiple models in your workspace. You can also provide an optional description to give more details about the purpose or characteristics of this model.
            <br />
            <Button
                sx={{ ...LinkSxSmall, padding: 0, minWidth: "0" }}
                onClick={() => {
                  fillExample();
                }}>
                Example
            </Button>
          </GuideCallout>
        } arrow>
          <TextField sx={InputBaseSx} value={nameValue} onChange={onChangeName} fullWidth className='' id="model-name-field" size="small" label="Name" variant="outlined" />
        </Tooltip>
        <TextField sx={InputBaseSx} size="small" value={description} onChange={onChangeDescription} fullWidth className='' id="trained-model-description-field" label="Description" variant="outlined" />
      </Box>
      <Box sx={{ ...FlexBoxColumn, alignItems: "flex-end", gap: "5px", marginBottom: "10px" }}>
        <Tooltip title={"Upload a CSV/JSON file to define the schema for your model"} placement="bottom" arrow>
          <IconButton sx={{ ...PrimaryBtnBorderlessSx, fontSize: Sizes.icon, p: "5px", border: "none" }} size="medium" onClick={handleLoadFile}>
            <UploadFileIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
      <Divider />
      <StructureItem />

      <Box className='additional-actions' sx={{ paddingTop: "15px", paddingBottom: "15px" }}>
        <ContextHelper content={<>A schema item represents a single input (Feature), output (Target) or info field in your model. Each item has a name and a data type (e.g. number, string, boolean) that defines how the model will use this value during training and prediction.</>}>
          <Button sx={{ ...SecondaryBtnSx, border: "none" }} onClick={onAddItem} startIcon={<AddIcon />}>Add new item</Button>
        </ContextHelper>
      </Box>
      <input
        type="file"
        ref={fileInputRef}
        accept=".csv,.json,text/csv,application/json"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        aria-label="Upload CSV or JSON file"
      />
    </ModalBase>
    <Component {...props as T} />
  </>
}


export const StructureItem = () => {
  const dispatch = useDispatch();
  const items = useSelector(getSchemaItems);
  const contextHelperContent = React.useCallback((type: StructureColumnTypeEnum) => {
    switch (type) {
      case StructureColumnTypeEnum.Feature:
        return <>Features are input variables used by the model to learn patterns and make predictions. Each feature should represent meaningful information for the model, such as age, price, or usage hours.</>;
      case StructureColumnTypeEnum.Target:
        return <>Targets are the values the model is trained to predict. For example, in a churn model, the target could be whether the customer will leave (true/false).</>;
      case StructureColumnTypeEnum.Info:
        return <>Info fields store additional data (like IDs or reference values) that are saved with the dataset but are not used for training or prediction.</>;
    }
  }, []);
  return <>
    {items.map((x, index) => {

      const onChangeType = (e: SelectChangeEvent<StructureItemType>) => {
        const newValueType = e.target.value as StructureItemType;
        const defaultCategories: SchemaItemCategory[] = [{ value: "false", index: 0 }, { value: "true", index: 1 }];
        const newItem = {
          index: index,
          name: x.name,
          valueTypeId: newValueType,
          typeId: x.typeId,
          refSchemaId: undefined,
          categories: newValueType === StructureItemType.Category
            ? (x.categories && x.categories.length > 0 ? x.categories : defaultCategories)
            : undefined,
          arrayLength: newValueType === StructureItemType.NumberArray ? (x.arrayLength || 0) : undefined
        };
        dispatch(updateStructureItem(newItem));
      }

      const onChangeName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        dispatch(updateStructureItem({ index: index, name: e.target.value, valueTypeId: x.valueTypeId, typeId: x.typeId, refSchemaId: x.refSchemaId, categories: x.categories, arrayLength: x.arrayLength }))
      }

      const onChangeLength = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (!isNaN(Number(e.target.value))) {
          dispatch(updateStructureItem({ ...x, index: index, arrayLength: Number(e.target.value) }))
        }
      }

      const onChangeColumnType = (e: SelectChangeEvent<StructureColumnTypeEnum>) => {
        dispatch(updateStructureItem({ index: index, name: x.name, valueTypeId: x.valueTypeId, refSchemaId: x.refSchemaId, typeId: e.target.value as StructureColumnTypeEnum, categories: x.categories, arrayLength: x.arrayLength }))
      }

      const onUpdateCategories = (categories: SchemaItemCategory[]) => {
        dispatch(updateStructureItem({ ...x, index: index, categories }));
      }

      const onRemoveItem = () => dispatch(removeStructureItem(index))

      return <div className='structure-item' key={index}>
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: '100%', justifyContent: "center", alignItems: "center" }}
        >
          <TextField sx={InputBaseSx} size="small" fullWidth value={x.name} onChange={onChangeName} className="model-name-field" label="Stucture Item Name" variant="outlined" />

          <FormControl sx={{ ...InputBaseSx, minWidth: "150px" }}>
            <InputLabel id="demo-simple-select-label">Value type</InputLabel>
            <Select
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={x.valueTypeId}
              size="small"
              label="Value type"
              MenuProps={SelectMenuProps}
              onChange={onChangeType}
            >
              {Object.keys(StructureItemType).map(x => <MenuItem sx={SelectMenuItemSx} key={x} value={x}>{x}</MenuItem>)}
            </Select>
          </FormControl>

          <ContextHelper content={contextHelperContent(x.typeId)}>
            <FormControl sx={{ ...InputBaseSx, minWidth: "150px" }}>
              <InputLabel id="ColumnItemTypeLabel">Item type</InputLabel>
              <Select
                labelId="ColumnItemTypeLabel"
                id="ColumnItemType"
                size="small"
                value={x.typeId}
                label="Item type"
                onChange={onChangeColumnType}
                MenuProps={SelectMenuProps}
              >
                {Object.keys(StructureColumnTypeEnum).map(x => <MenuItem sx={SelectMenuItemSx} key={x} value={x}>{x.replace(/([a-z])([A-Z])/g, '$1 $2').trim()}</MenuItem>)}
              </Select>
            </FormControl>
          </ContextHelper>

          <Tooltip title={"Remove item"} placement="bottom" arrow>
            <IconButton sx={{ ...SecondaryBtnSx, p: "5px", border: "none" }} aria-label="delete" onClick={onRemoveItem}>
              <ClearIcon sx={{ fontSize: Sizes.icon }} />
            </IconButton>
          </Tooltip>

        </Stack>
        {x.valueTypeId === StructureItemType.NumberArray && <TextField sx={{ ...InputBaseSx, marginBottom: 0 }} size="small" value={x.arrayLength} onChange={onChangeLength} className="model-name-field" label="Length" variant="outlined" />}
        {x.valueTypeId === StructureItemType.Category && (
          <CategoryValuesEditor
            categories={x.categories || []}
            onUpdateCategories={onUpdateCategories}
          />
        )}
        {x.valueTypeId === StructureItemType.Model && (
          <TextField
            sx={{ ...InputBaseSx, marginBottom: 0 }}
            size="small"
            value={x.refSchemaId}
            onChange={
              (e) => dispatch(updateStructureItem({ ...x, index: index, refSchemaId: e.target.value }))
            }
            label="Schema Id"
            variant="outlined" />
        )}
      </div>
    })}
  </>;
}

type CategoryValuesEditorProps = {
  categories: SchemaItemCategory[];
  onUpdateCategories: (categories: SchemaItemCategory[]) => void;
}

const CategoryValuesEditor = ({ categories, onUpdateCategories }: CategoryValuesEditorProps) => {
  const [inputValue, setInputValue] = React.useState('');
  const hasMinimumCategories = categories.length >= 2;

  const handleAddCategory = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !categories.some(c => c.value.toLowerCase() === trimmedValue.toLowerCase())) {
      onUpdateCategories([...categories.map((x, index) => ({ ...x, index })), { value: trimmedValue, index: categories.length }]);
      setInputValue('');
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCategory();
    }
  }

  const handleRemoveCategory = (valueToRemove: string) => {
    onUpdateCategories(categories.filter(c => c.value !== valueToRemove).map((x, index) => ({ ...x, index })));
  }

  return (
    <Box sx={{ ...FlexBoxColumn, gap: "10px" }}>
      <ContextHelper content={
        <>
          Category values define the possible options for this field. For example, 
          a "Color" category might have values like "Red", "Green", "Blue". You 
          need at least 2 category values. Duplicate values are not allowed, and the comparison is case-insensitive (e.g. "Red" and "red" would be considered duplicates).
          <br />
          <strong>Note:</strong> For binary classification, the <strong>first</strong> 
          category value (index 0) is treated as the <strong>negative</strong> class, 
          and the <strong>second</strong> (index 1) as the <strong>positive</strong> class.
        </>}>
        <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
        }}>
          <TextField
            sx={{ ...InputBaseSx, margin: 0, flex: 1 }}
            size="small"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            label="Add category value"
            variant="outlined"
            placeholder="Type and press Enter"
            error={!hasMinimumCategories}
            helperText={!hasMinimumCategories ? "At least 2 category values required" : ""}
          />
          <Tooltip title="Add category value" placement="bottom" arrow>
            <IconButton
              sx={{ ...PrimaryBtnBorderlessSx, fontSize: Sizes.icon, p: "5px", border: "none" }}
              onClick={handleAddCategory}
              disabled={!inputValue.trim()}
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </ContextHelper>
      <Box sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        minHeight: "32px"
      }}>
        {categories.map((category, idx) => (
          <Chip
            key={idx}
            label={
              <>
                <span style={{ color: Palette.textColorSecondary, fontSize: Sizes.textSecondary }}>
                  {`${category.index}: `}
                </span>
                {category.value}
              </>
            }
            onDelete={() => handleRemoveCategory(category.value)}
            size="small"
            sx={{
              backgroundColor: Palette.backgroundColorPrimary,
              color: Palette.textColorPrimary,
              fontSize: Sizes.textPrimary,
              border: Palette.borderPrimary,
              "& .MuiChip-deleteIcon": {
                color: Palette.textColorSecondary,
                fontSize: Sizes.icon,
                "&:hover": {
                  color: Palette.btnRemove
                }
              }
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
